import { useTranslation } from "react-i18next"

import { FileText, X } from "@/components/icons"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Switch } from "@/components/ui/switch"
import { cn } from "@/lib/utils"

type FileListProps = {
  files: File[]
  overrideFileIndexes: number[]
  onRemove: (index: number) => void
  onToggleOverride: (index: number, checked: boolean) => void
}

export function FileList({
  files,
  overrideFileIndexes,
  onRemove,
  onToggleOverride,
}: FileListProps) {
  const { t } = useTranslation()
  if (files.length === 0) return null
  return (
    <ul className="mt-2 space-y-1">
      {files.map((file, i) => (
        <li
          key={`${file.name}-${i}`}
          className={cn(
            "bg-muted/60 flex items-center gap-2 rounded-md px-2.5 py-1.5 text-sm",
            overrideFileIndexes.includes(i) && "bg-warning/10 dark:bg-warning/20"
          )}
        >
          <FileText className="text-muted-foreground size-3.5 shrink-0" />
          <span className="min-w-0 flex-1 truncate">{file.name}</span>
          {overrideFileIndexes.includes(i) && (
            <Badge
              className="border-warning bg-warning/20 text-warning-foreground dark:border-warning dark:bg-warning/40 dark:text-warning-foreground shrink-0 border font-normal"
              variant="outline"
            >
              {t("tender.quickStart.overrideBadge")}
            </Badge>
          )}
          <div className="flex items-center gap-1.5">
            <span className="text-muted-foreground text-xs">{t("tender.quickStart.prio")}</span>
            <Switch
              checked={overrideFileIndexes.includes(i)}
              onCheckedChange={(checked) => onToggleOverride(i, checked)}
            />
          </div>
          <span className="text-muted-foreground shrink-0 text-xs tabular-nums">
            {Math.round(file.size / 1024)} KB
          </span>
          <Button
            aria-label={t("tender.quickStart.removeFileAria", { name: file.name })}
            className="text-muted-foreground hover:text-destructive size-7 shrink-0 p-0"
            size="icon"
            type="button"
            variant="ghost"
            onClick={() => onRemove(i)}
          >
            <X className="size-3.5" />
          </Button>
        </li>
      ))}
    </ul>
  )
}
